import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAgentReport, getLocationReport } from "../../pages/dashboard/api";

export interface LocationData {
    id: number;
    location: string;
    agency: string;
    tenant: string;
    agents_total: number;
    agents_active_yesterday: number;
    agents_active_last_week: number;
    agents_active_last_month: number;
}

export interface AgentData {
    id: number;
    name: string;
    email: string;
    company_username: string;
    team: string;
    location: string;
    agency: string;
    tenant: string;
    created: string;
    last_login: string | null;
    last_call: string | null;
}

interface ReportState {
    locationData: LocationData[];
    agentData: AgentData[];
    scorecardsData: { [key: string]: number };
    agentActivityPerDay: { [date: string]: number };
    loadingLocations: boolean;
    loadingAgents: boolean;
    error: string | null;
}

const initialState: ReportState = {
    locationData: [],
    agentData: [],
    scorecardsData: {},
    agentActivityPerDay: {},
    loadingLocations: false,
    loadingAgents: false,
    error: null,
};

export const fetchLocationData = createAsyncThunk("report/fetchLocationData", async () => {
    return await getLocationReport();
});

export const fetchAgentData = createAsyncThunk("report/fetchAgentData", async (agentFilterValues: string[]) => {
    return await getAgentReport(agentFilterValues);
});

const dashboardSlice = createSlice({
    name: "report",
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            .addCase(fetchLocationData.pending, (state) => {
                state.loadingLocations = true;
                state.error = null;
            })
            .addCase(fetchLocationData.fulfilled, (state, action) => {
                state.loadingLocations = false;
                state.locationData = action.payload.locations;
                state.scorecardsData = action.payload.scorecards;
                state.agentActivityPerDay = action.payload.agent_activity_per_day;
            })
            .addCase(fetchLocationData.rejected, (state, action) => {
                state.loadingLocations = false;
                state.error = action.error.message || "Failed to fetch location data";
            })
            .addCase(fetchAgentData.pending, (state) => {
                state.loadingAgents = true;
                state.error = null;
            })
            .addCase(fetchAgentData.fulfilled, (state, action) => {
                state.loadingAgents = false;
                state.agentData = action.payload.agents;
            })
            .addCase(fetchAgentData.rejected, (state, action) => {
                state.loadingAgents = false;
                state.error = action.error.message || "Failed to fetch agent data";
            });
    },
});

export const { reducer } = dashboardSlice;
